import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updatePlanningTodo, updateHabit, toggleHabitLog, addNodeUpdate } from '../actions/planningAction';
import './FocusWidget.css';

export const FOCUS_SESSION_KEY = 'dmyor_focus_session';

export function writeFocusSession(data) {
  if (data) {
    localStorage.setItem(FOCUS_SESSION_KEY, JSON.stringify(data));
  } else {
    localStorage.removeItem(FOCUS_SESSION_KEY);
  }
  window.dispatchEvent(new CustomEvent('dmyor-focus-update', { detail: data }));
}

function readSession() {
  try { return JSON.parse(localStorage.getItem(FOCUS_SESSION_KEY)); } catch { return null; }
}

function fmt(ms) {
  const s = Math.ceil(Math.max(0, ms) / 1000);
  const m = Math.floor(s / 60), sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// A focus session runs against either a todo or a habit (session.kind).
// Both persist focusStartedAt/focusDurationMins/focusStatus the same way;
// this just picks the right update action for whichever entity is active.
function patchEntity(dispatch, session, patch) {
  return session.kind === 'habit'
    ? dispatch(updateHabit(session.entityId, patch))
    : dispatch(updatePlanningTodo(session.entityId, patch));
}

export default function FocusWidget() {
  const dispatch = useDispatch();
  const [session, setSession]       = useState(() => readSession());
  const [expanded, setExpanded]     = useState(false);
  const [left, setLeft]             = useState(0);
  const [showUpdate, setShowUpdate] = useState(false);
  const [updateText, setUpdateText] = useState('');
  const [loading, setLoading]       = useState(false);

  useEffect(() => {
    const onCustom = (e) => {
      const data = e.detail ?? null;
      setSession(data);
      if (!data) { setExpanded(false); setShowUpdate(false); setUpdateText(''); }
    };
    const onStorage = (e) => {
      if (e.key === FOCUS_SESSION_KEY) {
        const data = e.newValue ? JSON.parse(e.newValue) : null;
        setSession(data);
        if (!data) { setExpanded(false); setShowUpdate(false); setUpdateText(''); }
      }
    };
    window.addEventListener('dmyor-focus-update', onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('dmyor-focus-update', onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    if (!session) { setLeft(0); return; }
    const endMs = new Date(session.focusStartedAt).getTime() + session.focusDurationMins * 60000;
    const tick = () => setLeft(Math.max(0, endMs - Date.now()));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [session]);

  const expired = !!session && left <= 0;

  const extend = useCallback(async () => {
    if (!session || loading) return;
    setLoading(true);
    const newDuration = (session.focusDurationMins || 25) + 15;
    const updated = { ...session, focusDurationMins: newDuration };
    writeFocusSession(updated);
    try { await patchEntity(dispatch, session, { focusDurationMins: newDuration }); } catch (_) {}
    setLoading(false);
  }, [session, loading, dispatch]);

  const clearSession = useCallback(async (status = 'completed') => {
    if (!session || loading) return;
    setLoading(true);
    const s = session;
    writeFocusSession(null);
    try {
      await patchEntity(dispatch, s, {
        focusStartedAt: null,
        focusDurationMins: null,
        focusStatus: status,
      });
    } catch (_) {}
    setLoading(false);
  }, [session, loading, dispatch]);

  // For a todo, a written update is required. For a habit, completing the
  // session is enough on its own — the note is optional.
  const canPostUpdate = session?.kind === 'habit' || !!updateText.trim();

  const postUpdate = useCallback(async () => {
    if (!session || loading) return;
    if (session.kind !== 'habit' && !updateText.trim()) return;
    setLoading(true);
    try {
      if (session.nodeId && updateText.trim()) await dispatch(addNodeUpdate(session.nodeId, updateText.trim()));
      // Completing a focus session on a habit means the habit was done today.
      if (session.kind === 'habit') await dispatch(toggleHabitLog(session.entityId, todayKey()));
    } catch (_) {}
    await clearSession('completed');
    setLoading(false);
  }, [session, updateText, loading, dispatch, clearSession]);

  if (!session) return null;

  const totalMs   = session.focusDurationMins * 60000;
  const fracLeft  = totalMs > 0 ? Math.min(1, Math.max(0, left / totalMs)) : 0;

  // SVG ring geometry (expanded view)
  const R    = 52;
  const CIRC = 2 * Math.PI * R;
  const dashOffset = CIRC * (1 - fracLeft);

  return (
    <div className={`fw-widget${expanded ? ' fw-widget--expanded' : ''}${expired ? ' fw-widget--done' : ''}`}>
      {!expanded ? (
        <div className="fw-compact" onClick={() => setExpanded(true)} title="Click to expand">
          <span className="fw-compact-bolt"><i className="bx bxs-bolt" /></span>
          <div className="fw-compact-main">
            <span className={`fw-compact-clock${expired ? ' fw-compact-clock--done' : ''}`}>
              {expired ? "Time's up" : fmt(left)}
            </span>
            <span className="fw-compact-text">{session.text}</span>
          </div>
          <span className="fw-compact-expand"><i className="bx bx-chevron-up" /></span>
          <span className="fw-compact-bar" style={{ transform: `scaleX(${fracLeft})` }} />
        </div>
      ) : (
        <div className="fw-panel">
          <div className="fw-panel-header">
            <span className="fw-panel-eyebrow">
              <i className="bx bxs-bolt" /> Focus
            </span>
            <button className="fw-collapse-btn" onClick={() => setExpanded(false)} title="Minimize">
              <i className="bx bx-chevron-down" />
            </button>
          </div>

          <div className="fw-ring-wrap">
            <svg className="fw-ring" width="128" height="128" viewBox="0 0 128 128">
              <circle className="fw-ring-track" cx="64" cy="64" r={R} strokeWidth="7" fill="none" />
              <circle
                className="fw-ring-fill"
                cx="64" cy="64" r={R} strokeWidth="7" fill="none"
                strokeDasharray={CIRC}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                transform="rotate(-90 64 64)"
              />
            </svg>
            <div className="fw-ring-center">
              {!expired ? (
                <>
                  <span className="fw-ring-clock">{fmt(left)}</span>
                  <span className="fw-ring-sub">remaining</span>
                </>
              ) : (
                <>
                  <span className="fw-ring-clock fw-ring-clock--done">00:00</span>
                  <span className="fw-ring-sub fw-ring-sub--done">time's up</span>
                </>
              )}
            </div>
          </div>

          <div className="fw-panel-todo">{session.text}</div>

          {!showUpdate ? (
            <div className="fw-actions">
              <button className="fw-action fw-action--primary" onClick={() => setShowUpdate(true)} disabled={loading}>
                {session.kind === 'habit' ? 'Mark done' : 'Post update'}
              </button>
              <div className="fw-actions-row">
                <button className="fw-action fw-action--ghost" onClick={extend} disabled={loading}>
                  <i className="bx bx-plus" /> 15 min
                </button>
                <button className="fw-action fw-action--fail" onClick={() => clearSession('failed')} disabled={loading}>
                  <i className="bx bx-x" /> Failed
                </button>
              </div>
            </div>
          ) : (
            <div className="fw-update-form">
              <textarea
                className="fw-update-input"
                placeholder={session.kind === 'habit' ? "What did you do? (optional)" : "What did you finish or learn?"}
                value={updateText}
                onChange={e => setUpdateText(e.target.value)}
                autoFocus
                rows={3}
              />
              <div className="fw-actions-row">
                <button className="fw-action fw-action--ghost" onClick={() => setShowUpdate(false)} disabled={loading}>
                  Back
                </button>
                <button
                  className="fw-action fw-action--primary"
                  disabled={!canPostUpdate || loading}
                  onClick={postUpdate}
                >
                  {loading ? 'Saving…' : session.kind === 'habit' ? 'Mark done' : 'Post update'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
